import { Button } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import { ClassName } from 'types/ClassName';
import { DetailsCard, HeaderDialog, PanelContent } from 'components';
import DraggableList, { IUpdatedDraggable } from 'components/foundation/DraggableList/draggable-list/DraggableList';
import { useOpenable, useOptimisticState, usePositionQuery, usePositionUpdate } from 'hooks';
import { find, maxBy } from 'lodash';
import { useCallback, useState } from 'react';
import IPosition from 'types/Position';
import { useFeatureFlags } from 'utils/features';
import { t } from 'utils/localize';
import CreatePositionButton from '../components/buttons/CreatePositionButton';
import PositionDetails from './PositionDetails';
import './Positions.scss';
import PositionDetailsTable from './components/PositionDetailsTable/PositionDetailsTable';
import FullContentHeaderDialogContent from 'components/foundation/dialogs/FullContentHeaderDialogContent/FullContentHeaderDialogContent';

export interface IPositionsProps {
  className?: ClassName;
}

const Positions = (props: IPositionsProps) => {
  const [targetPosition, setTargetPosition] = useState<IPosition>();
  const { positions, refetch } = usePositionQuery();
  const [updateKey] = usePositionUpdate();
  const { className } = props;
  const classes = classNames('settings-positions', className);
  const newLevel = maxBy(positions, 'level')?.level;
  const positionDetails = useOpenable();
  const isPro = useFeatureFlags('PRO');
  const [optimisticPositions, setOptimisticPositions] = useOptimisticState(positions);
  const positionQuickViewOpenable = useOpenable();
  const [scroller, setScroller] = useState<HTMLElement | null>(null);

  const positionsWithEnabled = optimisticPositions?.map((position) => ({
    ...position,
    draggable: isPro && position.level !== 1,
  }));

  const onUpdate = useCallback(
    (updatedItems: Array<IUpdatedDraggable<IPosition>>) => {
      setOptimisticPositions(
        updatedItems.map((item) => {
          if (item.payload.level !== 1) {
            updateKey(item.payload.id, 'level', item.index + 1);
          }

          return { ...item.payload, level: item.index + 1 };
        })
      );

      refetch();
    },
    [refetch, setOptimisticPositions, updateKey]
  );

  async function handleUpdate() {
    return refetch().then((updatedPositions) => {
      if (targetPosition) {
        const updatedPosition = find(updatedPositions.data.positions, { id: targetPosition.id });

        if (updatedPosition) {
          setTargetPosition(updatedPosition);
        }
      } else {
        setTargetPosition(undefined);
      }
    });
  }

  const openPosition = useCallback(
    (position: IPosition) => {
      positionDetails.open();
      setTargetPosition(position);
    },
    [positionDetails]
  );

  const renderDraggableItem = useCallback((item: IPosition) => <div>{item.title}</div>, []);

  return (
    <div className={classes}>
      {isPro && newLevel ? (
        <CreatePositionButton className="mb-6 mr-6" highestLevel={newLevel + 1} onCreate={refetch} />
      ) : (
        <div></div>
      )}

      <Button type="secondary" onClick={positionQuickViewOpenable.open} className={isPro && newLevel ? '' : 'mb-6'}>
        {t('All Position Settings')}
      </Button>

      {positionsWithEnabled && (
        <DetailsCard>
          <DraggableList
            stroke={true}
            hover={true}
            renderItem={renderDraggableItem}
            items={positionsWithEnabled}
            onUpdate={onUpdate}
            onClick={openPosition}
            className="positions-list"
          />
        </DetailsCard>
      )}

      <HeaderDialog
        title={targetPosition?.title ?? ''}
        size="full"
        isOpen={positionDetails.isOpen}
        hasDismiss={true}
        onClose={positionDetails.close}
      >
        {targetPosition && (
          <PositionDetails position={targetPosition} onUpdate={handleUpdate} onClose={positionDetails.close} />
        )}
      </HeaderDialog>

      <HeaderDialog
        title={t('All Position Settings')}
        size="full"
        isOpen={positionQuickViewOpenable.isOpen}
        hasDismiss={true}
        onClose={positionQuickViewOpenable.close}
      >
        <FullContentHeaderDialogContent className="panel-content" ref={setScroller}>
          {scroller && <PositionDetailsTable scroller={scroller} />}
        </FullContentHeaderDialogContent>
      </HeaderDialog>
    </div>
  );
};

export default Positions;
